import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { financeApi } from '../../services/financeApi.js';

const PRIORITIES = ['LOW', 'MEDIUM', 'HIGH', 'URGENT'];
const empty = () => ({ equipmentId: '', title: '', priority: 'MEDIUM', intervalDays: '90', nextDue: new Date().toISOString().slice(0, 10) });

export function MaintenancePlanModal({ open, onClose, equipment }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(empty);
  const [error, setError] = useState(null);

  useEffect(() => { if (open) { setForm(empty()); setError(null); } }, [open]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = useMutation({
    mutationFn: (body) => financeApi.createMaintenancePlan(body),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['finance', 'maintenance-plans'] }); onClose(); },
    onError: (err) => setError(err?.message || t('common.loadError')),
  });

  function submit(e) {
    e.preventDefault();
    setError(null);
    if (!form.equipmentId || !form.title.trim() || !form.intervalDays || !form.nextDue) { setError(t('pm.error.plan')); return; }
    save.mutate({ equipmentId: form.equipmentId, title: form.title.trim(), priority: form.priority, intervalDays: Number(form.intervalDays), nextDue: form.nextDue });
  }

  return (
    <Modal open={open} onClose={onClose} title={t('pm.planModal.title')}
      footer={<><Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="primary" onClick={submit} disabled={save.isPending}>{save.isPending ? `${t('common.save')}…` : t('common.save')}</Button></>}>
      <form onSubmit={submit} className="form-stack">
        <label className="field">
          <span className="field-label">{t('pm.col.equipment')}</span>
          <select className="input" value={form.equipmentId} onChange={set('equipmentId')} required>
            <option value="">-</option>
            {(equipment || []).map((e) => <option key={e.id} value={e.id}>{e.equipmentNumber} · {e.name}</option>)}
          </select>
        </label>
        <label className="field">
          <span className="field-label">{t('pm.col.title')}</span>
          <input className="input" value={form.title} onChange={set('title')} required />
        </label>
        <div className="field-row">
          <label className="field" style={{ maxWidth: 160 }}>
            <span className="field-label">{t('pm.col.priority')}</span>
            <select className="input" value={form.priority} onChange={set('priority')}>
              {PRIORITIES.map((p) => <option key={p} value={p}>{t(`pm.priority.${p}`)}</option>)}
            </select>
          </label>
          <label className="field" style={{ maxWidth: 150 }}>
            <span className="field-label">{t('pm.plan.interval')}</span>
            <input className="input num" type="number" min="1" max="3650" value={form.intervalDays} onChange={set('intervalDays')} required />
          </label>
          <label className="field">
            <span className="field-label">{t('pm.plan.nextDue')}</span>
            <input className="input" type="date" value={form.nextDue} onChange={set('nextDue')} required />
          </label>
        </div>
        {error && <div className="alert danger">{error}</div>}
      </form>
    </Modal>
  );
}
